import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

const helpers = createMultiStyleConfigHelpers(['root', 'container', 'itemWrapper', 'closeButton']);

/**
 * Color mapping for backgroundColor field from Contentful
 * Supports both camelCase and display names (with spaces)
 */
const backgroundColorMap = {
  // Standard keys
  black: 'greyscale.100',           // #1B1A18
  charcoal: 'brandColors.charcoal', // #282829
  white: 'white',
  paleLinen: 'primary.paleLinen',   // #F7F5F0
  linen: 'primary.linen100',        // #C6B5A1
  saleRed: 'secondary.newSaleRed',  // #BF1619
  // Display name variants (from Contentful dropdown)
  'Black': 'greyscale.100',
  'Charcoal': 'brandColors.charcoal',
  'White': 'white',
  'Pale Linen': 'primary.paleLinen',
  'Linen': 'primary.linen100',
  'Sale Red': 'secondary.newSaleRed',
};

/**
 * Text color mapping based on background - for close button
 */
const getTextColor = (backgroundColor) => {
  const darkBackgrounds = ['black', 'Black', 'charcoal', 'Charcoal', 'saleRed', 'Sale Red'];
  return darkBackgrounds.includes(backgroundColor) ? 'white' : 'text.primary';
};

/**
 * Fixed height for promo banner to prevent layout shift during animation
 */
const BANNER_HEIGHT = '40px';

/**
 * @typedef {import('@chakra-ui/styled-system').PartsStyleInterpolation<{
 *  keys: ("root" | "container" | "itemWrapper" | "closeButton")[]
 * }>} PromoBannerStyles
 */

export const promoBannerStyles = helpers.defineMultiStyleConfig({
  /**
   * Base styles for PromoBanner component
   *
   * @param {Object} args - Component arguments
   * @param {Contentful.PromoBanner} args.component - PromoBanner component data
   * @param {string} [args.backgroundColor] - Background color from Contentful
   *
   * @returns {PromoBannerStyles} - Component styles
   */
  baseStyle({component, backgroundColor}) {
    const bgColor = backgroundColorMap[backgroundColor] || 'greyscale.100';
    const textColor = getTextColor(backgroundColor);

    return {
      root: {
        width: '100%',
        backgroundColor: bgColor,
        py: 2,
        px: 4,
        position: 'relative',
        zIndex: 'banner',
        height: BANNER_HEIGHT,
        overflow: 'hidden',
      },
      container: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        maxWidth: 'container.xxxl',
        mx: 'auto',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
      },
      itemWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
      },
      closeButton: {
        position: 'absolute',
        right: 4,
        top: '50%',
        transform: 'translateY(-50%)',
        bg: 'transparent',
        border: 'none',
        minW: 'auto',
        h: 'auto',
        p: 1,
        _hover: {
          bg: 'transparent',
          opacity: 0.7,
        },
        _active: {
          bg: 'transparent',
        },
        _focus: {
          boxShadow: 'none',
        },
      },
    };
  },

  variants: {
    default: {},
  },
});
